import React, { useRef, useEffect, useCallback, useState } from 'react'

import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow'
import vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow'
import vtkRenderWindowInteractor from '@kitware/vtk.js/Rendering/Core/RenderWindowInteractor'
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer'

// vtkjs file types
import HttpSceneLoader from '@kitware/vtk.js/IO/Core/HttpSceneLoader'
import { get } from '@kitware/vtk.js/IO/Core/DataAccessHelper.js'

// Give DataAccessHelper to access to various data source
// import '@kitware/vtk.js/IO/Core/DataAccessHelper/ZipDataAccessHelper'
import '@kitware/vtk.js/IO/Core/DataAccessHelper/HtmlDataAccessHelper'
import '@kitware/vtk.js/IO/Core/DataAccessHelper/JSZipDataAccessHelper'
import vtkXMLPolyDataReader from '@kitware/vtk.js/IO/XML/XMLPolyDataReader'

// opengl vs webgpu geometry profiles
import '@kitware/vtk.js/Rendering/Profiles/Geometry'
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor'
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper'
import vtkOrientationMarkerWidget from '@kitware/vtk.js/Interaction/Widgets/OrientationMarkerWidget'

import colorMaps from './colorMaps'
import { ColorMode, ScalarMode } from '@kitware/vtk.js/Rendering/Core/Mapper/Constants'
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction'
import vtkScalarBarActor from '@kitware/vtk.js/Rendering/Core/ScalarBarActor'
import vtkInteractorStyleManipulator from '@kitware/vtk.js/Interaction/Style/InteractorStyleManipulator'
import vtkGestureCameraManipulator from '@kitware/vtk.js/Interaction/Manipulators/GestureCameraManipulator'
import vtkInteractionPresets from '@kitware/vtk.js/Interaction/Style/InteractorStyleManipulator/Presets'
import vtkImplicitPlaneWidget from '@kitware/vtk.js/Widgets/Widgets3D/ImplicitPlaneWidget'

import {compassRoseIndicator, brandingActor} from './orientationIndicator'

import { vtkContext, vtkReactState } from './useVTK.types'

import { vtkReducer } from './reducer'

import { COLORSET_REGEXP, DEFAULT_COLORSET, LEGEND_DEFAULTS } from './enums'

import { RENDERER, SCENE, LEGEND, WIDGET_MANAGER, deriveVTKState } from '../../lib/deriveVTKState'
import vtkPlane from '@kitware/vtk.js/Common/DataModel/Plane'
import { Bounds, Vector3 } from '@kitware/vtk.js/types'
import { vec3 } from 'gl-matrix'

import vtkWidgetManager from '@kitware/vtk.js/Widgets/Core/WidgetManager'
import vtkBoundingBox from '@kitware/vtk.js/Common/DataModel/BoundingBox'

import debounce from 'lodash.debounce'
import brandingWidget from './brandingWidget'
import vtkSphereSource from '@kitware/vtk.js/Filters/Sources/SphereSource'
import chroma from 'chroma-js'

import lbtColorMaps from './lbtColorMaps.json'
import { legendAutoLayout, generateTicks } from './legendLayout'

import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray'
import vtkLookupTable from '@kitware/vtk.js/Common/Core/LookupTable'

// uuid function
// https://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid
function uuid() {
  let d = new Date().getTime()//Timestamp
  let d2 = (performance && performance.now && (performance.now() * 1000)) || 0//Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16//random number between 0 and 16
    if (d > 0) {//Use timestamp until depleted
      r = (d + r) % 16 | 0
      d = Math.floor(d / 16)
    } else {//Use microseconds since page-load if supported
      r = (d2 + r) % 16 | 0
      d2 = Math.floor(d2 / 16)
    }
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

const interactorStyleDefinitions = [
  { type: 'unicamRotate', options: { button: 1, focusSphereRadiusFactor: 0.6, displayFocusSphereOnButtonDown: false } }, // Rotate on Left button drag
  { type: 'pan', options: { button: 1, shift: true } }, // Pan on Shift + Left button drag
  { type: 'roll', options: { button: 1, control: true } },
  { type: 'rotate', options: { button: 1, alt: true } }, // Rotate on Left button drag
  { type: 'pan', options: { button: 3 } }, // Pan on Right button drag
  { type: 'zoomToMouse', options: { dragEnabled: false, scrollEnabled: true } }, // Zoom on scroll
]

const renderWindowSetup = (container: HTMLDivElement) => {
  // Standard rendering code setup
  const renderWindow = vtkRenderWindow.newInstance()
  const renderer = vtkRenderer.newInstance({})
  renderer.setBackgroundFrom([1.0, 1.0, 1.0, 1.0])

  renderWindow.addRenderer(renderer)

  // Use OpenGL as the backend to view the all this
  const openglRenderWindow = vtkOpenGLRenderWindow.newInstance()
  renderWindow.addView(openglRenderWindow)

  openglRenderWindow.setContainer(container)

  // Capture size of the container and set it to the renderWindow
  const { width, height } = container.getBoundingClientRect()
  openglRenderWindow.setSize(width, height)

  const interactor = vtkRenderWindowInteractor.newInstance()
  interactor.initialize()
  interactor.setView(openglRenderWindow)
  interactor.bindEvents(container)

  const interactorStyle = vtkInteractorStyleManipulator.newInstance()

  interactor.setInteractorStyle(interactorStyle)
  vtkInteractionPresets.applyDefinitions(interactorStyleDefinitions, interactorStyle)

  // Always add gesture
  interactorStyle.addGestureManipulator(
    vtkGestureCameraManipulator.newInstance()
  )

  // TODO: this works to customize the focus dot but, it's not working in ortho mode, focusSphere is huge
  // Customize focus dot
  const sphereSource = vtkSphereSource.newInstance()
  sphereSource.setRadius(0.5) 
  sphereSource.setThetaResolution(18)
  sphereSource.setPhiResolution(18)
  const sphereMapper = vtkMapper.newInstance()
  sphereMapper.setInputConnection(sphereSource.getOutputPort())

  const focusSphere = vtkActor.newInstance()
  focusSphere.setMapper(sphereMapper)
  const [r, g, b] = chroma('#1b90ff').gl()
  focusSphere.getProperty().setColor(r, g, b)
  focusSphere.getProperty().setAmbient(1)
  focusSphere.getProperty().setDiffuse(0)
  focusSphere.getProperty().setOpacity(0.75)
  
  focusSphere.setVisibility(false)

  interactorStyle.getMouseManipulators()
    .filter(m => m.isA('vtkMouseCameraUnicamRotateManipulator'))[0]
    .set({ 'focusSphere' : focusSphere })

  return { renderer, renderWindow, interactor }
}

const addSectionPlane = (renderer: vtkRenderer, widgetManager: vtkWidgetManager, normal: Vector3 = [0, 0, 1]) => {
  const widget = vtkImplicitPlaneWidget.newInstance()
  // widget.setNormalVisible(false)
  // widget.setOriginVisible(false)
  
  const bounds = [-1, 1, -1, 1, -1, 1]
  const min = vtkBoundingBox.getMinPoint([...bounds])
  const max = vtkBoundingBox.getMaxPoint([...bounds])
  
  const center = vtkBoundingBox.getCenter(bounds)
  const origin = vec3.dot(normal, center) > 0 ? min : max

  widget.placeWidget(bounds)
  widget.setPlaceFactor(1)

  const widgetState = widget.getWidgetState()
  widgetState.setNormal(normal[0], normal[1], normal[2])
  widgetState.setOrigin(origin[0], origin[1], origin[2])

  const viewWidget = widgetManager.addWidget(widget)
  viewWidget.setVisibility(false)
  
  const plane = vtkPlane.newInstance({
    normal: widget.getWidgetState().getNormal(),
    origin: widget.getWidgetState().getOrigin()
  })

  const updatePlane = () => {
    // @ts-ignore
    const origin = viewWidget.getWidgetState().getOrigin()
    // @ts-ignore
    const normal = viewWidget.getWidgetState().getNormal()
    
    plane.setOrigin(origin)  
    plane.setNormal(normal)

    const bounds = [-1, 1, -1, 1, -1, 1]

    const planes =  widgetManager.getWidgets()
      .filter(widget => widget.isA('vtkPlaneWidget' ))
      // @ts-ignore
      .map(widget => widget.get('plane').plane)
      
    planes.forEach(plane => {
      const [x, y, z] = plane.getOrigin()
      vtkBoundingBox.addPoint(bounds, x, y, z)
    })

    planes.forEach(plane => {
      vtkBoundingBox.cutWithPlane(bounds, plane.getOrigin(), plane.getNormal())
    })

    widgetManager.getWidgets()
      .filter(widget => widget.isA('vtkPlaneWidget' ))
      .forEach(widget => {
        // @ts-ignore
        widget.getWidgetState().setBounds(bounds)
      })
  }

  viewWidget.onInteractionEvent(updatePlane)
  viewWidget.onModified(updatePlane)

  viewWidget.set( { 'plane': plane } )

  renderer.resetCamera()
  widgetManager.enablePicking()

  return { 
    viewWidget
  }
}

export const addLegend = (mapper, label, units, renderer, legendSlot, ordinalDict) => {
  const legendConfig = {
    ...LEGEND_DEFAULTS,
    generateTicks: generateTicks(ordinalDict),
    autoLayout: legendAutoLayout(legendSlot)
  }
  
  const scalarBarActor = vtkScalarBarActor.newInstance(legendConfig)

  scalarBarActor.setVisibility(true)
  scalarBarActor.setDrawNanAnnotation(false)
  scalarBarActor.set({ slot: legendSlot })
  scalarBarActor.set({ units })

  renderer.addActor(scalarBarActor)

  scalarBarActor.setMapper(mapper)
  scalarBarActor.setScalarsToColors(mapper.getLookupTable())
  scalarBarActor.setAxisLabel(label)

  return scalarBarActor
}

const configureData = (object, renderer, legendSlot) => {
  const { source, mapper, actor, defaultSettings } = object
  
  // supports both vtkjs and vtp file formats
  const cellData = !source.getArrays ?
    source.getCellData().getArrays().map((val: any) => ({ array: val.get(), name: val.getName(), location: 'cellData', enable: true })) :
    source.getArrays().filter((val: any) => val.location === 'cellData')
  
  // supports both vtkjs and vtp file formats
  const pointData = !source.getArrays ?
    source.getPointData().getArrays().map((val: any) => ({ array: val.get(), name: val.getName(), location: 'pointData', enable: true })) :
    source.getArrays().filter((val: any) => val.location === 'pointData')

  const scalarVisibility = (cellData.length > 0 || pointData.length > 0)

  if (!scalarVisibility) return

  const scalarOptions = [...cellData, ...pointData]
  const colorByOptions = scalarOptions.map(val => val.name)

  // TODO: bring back createDefaultSettings when implementing loading vtp files
  // let defaults = typeof defaultSettings !== 'undefined' ? defaultSettings : createDefaults(actor, scalarOptions[0].name.split(':')[1])
  const defaults = typeof defaultSettings !== 'undefined' ? defaultSettings : {}
  
  const property = actor.getProperty()
  property.setPointSize(Math.max(property.getPointSize(), 10))

  let dataRangeOptions = scalarOptions.reduce((p, val, i, a) => {

    let location = ''
    if (cellData.findIndex(item => item.name === val.name) > -1) location = 'CellData'
    if (pointData.findIndex(item => item.name === val.name) > -1) location = 'PointData'
    
    p[val.name] = { range: [0, 100], colorset: DEFAULT_COLORSET, units: undefined, location, reverse: false }

    if (location === '') return p

    // Seach for new or old legend metedata
    const defaultLegend = defaultSettings.metadata 
      ? defaultSettings.metadata.find(l => (val.name === l?.data_type?.name) 
        || (val.name === l?.legend_parameters?.title)
        || (val.name === 'generic_data')
        || (val.name === 'Data'))
      : defaultSettings.legends 
      ? defaultSettings.legends.find(l => l.name === val.name)
      : undefined

    const mapperSource = mapper.getInputData()

    // Solve VTK defect - override array names
    mapperSource[`get${location}`]().getArrayByIndex(i).setName(val.name)

    const activeArray = mapperSource[`get${location}`]().getArrayByIndex(i)
    let range = activeArray.getRange().slice(0, 3)

    if (defaultLegend) {
      // New legend schema
      if (defaultSettings.metadata != null) {
        const min = typeof defaultLegend.legend_parameters.min === 'number' 
          ? defaultLegend.legend_parameters.min : undefined
        const max = typeof defaultLegend.legend_parameters.max === 'number' 
          ? defaultLegend.legend_parameters.max : undefined
        
        if (typeof min === 'number' && typeof max === 'number') {
          range = [min, max]
        }
        
        const colors = (
          defaultLegend.legend_parameters && 
          defaultLegend.legend_parameters.colors &&
          defaultLegend.legend_parameters.colors.length > 0) ? 
          {
            ColorSpace: 'RGB',
            Name: 'Custom',
            NanColor: [0,0,1],
            RGBPoints: defaultLegend.legend_parameters.colors
            .reduce((p, c, i, arr) => {
              if (!p) {
                p = []
              }
              
              p.push(i / (arr.length - 1))
              p.push(c.r / 255)
              p.push(c.g / 255)
              p.push(c.b / 255)
              
              return p
            }, [])
          } :
          lbtColorMaps[0]
          
        p[val.name].colors = colors
        p[val.name].units = defaultLegend.unit
      } else {
        // Old legend schema
        range = defaultLegend.range
        p[val.name].colorset = defaultLegend.colors
        p[val.name].units = defaultLegend.unit
      }
    }
    p[val.name].range = range

    const dictInfo = defaultSettings?.metadata ? defaultSettings?.metadata
      .find(l => (val.name === l?.data_type?.name 
        || val.name === l?.legend_parameters?.title)) : undefined

    p[val.name].ordinalDictionary = dictInfo?.legend_parameters?.ordinal_dictionary
    p[val.name].legendSlot = legendSlot

    return p
  }, {})

  if (!Object.keys(dataRangeOptions).length) dataRangeOptions = null

  const colorBy = colorByOptions.find((val, i) => val === defaults.mapper.colorByArrayName) || 'Solid'
  const dataDisplayOptions = dataRangeOptions[colorBy]

  const colorMap = dataDisplayOptions.colors ? 'Custom' : 'Original Ladybug'

  // for data objects, prevent lighting
  property.setLighting(false)

  // There would be a bug with this pattern if there are two datasets with the
  // same name on cellData and point data
  let location = ''
  if (pointData.findIndex(item => item.name === colorBy) > -1) location = 'PointData'
  if (cellData.findIndex(item => item.name === colorBy) > -1) location = 'CellData'

  const colorMode = ColorMode.MAP_SCALARS
  const scalarMode = location === 'PointData' ?
    ScalarMode.USE_POINT_FIELD_DATA :
    ScalarMode.USE_CELL_FIELD_DATA
  const interpolateScalarsBeforeMapping = location === 'PointData'

  mapper.set({
    colorBy,
    colorMode,
    interpolateScalarsBeforeMapping,
    scalarMode,
    scalarVisibility
  })

  mapper.setUseLookupTableScalarRange(true)
  
  let preset = dataDisplayOptions.colors ? dataDisplayOptions.colors : colorMaps.getPresetByName(DEFAULT_COLORSET)

  // Old legend schema
  const searchDefaultColorMap = dataRangeOptions[colorBy].colorset || DEFAULT_COLORSET
  const defaultColorMapIndex = colorMaps.rgbPresetNames
    .findIndex(name => name.toLowerCase().replace(COLORSET_REGEXP, '')
    .includes(searchDefaultColorMap.toLowerCase().replace(COLORSET_REGEXP, '')))
  const defaultColorMap = defaultColorMapIndex !== -1 ? colorMaps.rgbPresetNames[defaultColorMapIndex] : DEFAULT_COLORSET

  let colorMapUsed = colorMap
  
  if (defaultColorMap !== DEFAULT_COLORSET) {
    colorMapUsed = defaultColorMap
    preset = colorMaps.getPresetReverse(defaultColorMap) 
  }

  const range = dataDisplayOptions.range
  const units = dataDisplayOptions.units
  mapper.set({ 'units': units, 'colorSet': colorMapUsed, 
    'dataRangeOptions': dataRangeOptions })

  if (dataRangeOptions[colorBy]?.ordinalDictionary) {
    const dictLen = Object.values(dataRangeOptions[colorBy]?.ordinalDictionary).length
    const lut = getDiscreteColors(preset, range, dictLen)
    mapper.setLookupTable(lut)
  } else {
    const lut = vtkColorTransferFunction.newInstance()
    lut.applyColorMap(preset)
    lut.setMappingRange(range[0], range[1])
    lut.updateRange()

    mapper.setLookupTable(lut)
  }

  const label = units ? `${colorBy} (${units})` : colorBy

  const legend = addLegend(mapper, label, units, renderer, 
    legendSlot, dataRangeOptions[colorBy]?.ordinalDictionary)

  const axisLabel = units ? `${colorBy} (${units})` : colorBy
  if (colorBy === 'Data') legend.setAxisLabel('')
  else legend.setAxisLabel(axisLabel)

  actor.set({ legend })
}

const getColor = (value: number, range: number[], colors: number[][]) => {
  const domain = getArrayFromRange(range, colors.length)
  if (value < domain[0]) return colors[0]
  if (value > domain[domain.length - 1]) return colors[domain.length - 1]
  
  let color
  domain.forEach((d, count) => {
    if (d <= value && value <= domain[count + 1])
    {
      color = calcColor(domain, value, count, colors)
    }
  })
  
  return color
}

const calcColor = (domain: number[], value: number, 
  colorIndex: number, colors: number[][]) => {
    const rangeMinP = domain[colorIndex]
    const rangeP = domain[colorIndex + 1] - rangeMinP
    
    let factor = (value - rangeMinP) / rangeP
    if (!Number.isFinite(factor)) factor = 0
    
    const [rMin, gMin, bMin, aMin] = colors[colorIndex]
  const [rMax, gMax, bMax, aMax] = colors[colorIndex + 1]

  const red = factor * (rMax - rMin) + rMin
  const green = factor * (gMax - gMin) + gMin
  const blue = factor * (bMax - bMin) + bMin

  return [red, green, blue, 255]
}

const getColorsFromRGBPoints = (rgbPoints: any) => {
  const chunkSize = 4
  const colors = rgbPoints.reduce((res: any[][], 
    item: any, index: number) => {
    const cIndex = Math.floor(index / chunkSize)
    if (!res[cIndex]) {
      res[cIndex] = []
    }
    res[cIndex].push(item)
    return res
  }, [])
  
  const res = []
  for (let index = 0; index < colors.length; index++) {
    const [val, r, g, b] = colors[index]
    const R = r * 255
    const G = g * 255
    const B = b * 255
    
    res.push([R, G, B, 255])
  }
  
  return res
}

const getArrayFromRange = (range: number[], numColors: number) => {
  const step = (range[1] - range[0]) / (numColors - 1)
  const n = range[0]
  return Array.from({length: numColors}, (x, i) => n + i * step)
}

export const getDiscreteColors = (preset: any, range: number[], minColorNumber: number = 11) => {
  let colors = getColorsFromRGBPoints(preset.RGBPoints)
  
  // Apply min color number
  const values = getArrayFromRange(range, minColorNumber)
  colors = values.map(v => getColor(v, range, colors))
  
  const table = vtkDataArray.newInstance({
    numberOfComponents: 4,
    size: colors.length * 4,
    dataType: "Uint8Array",
  })
  
  colors.forEach((c, i) => table.setTuple(i, c))
  
  let lut = vtkLookupTable.newInstance()
  
  lut.setNumberOfColors(colors.length)
  lut.setMappingRange(range[0], range[1])
  lut.setTable(table)
  
  return lut
}

export const updateSectionPlane = ( widget: vtkImplicitPlaneWidget, bounds: Bounds, renderer: vtkRenderer ) => {
  const min = vtkBoundingBox.getMinPoint([...bounds])
  const max = vtkBoundingBox.getMaxPoint([...bounds])
  const center = vtkBoundingBox.getCenter(bounds)
  
  const normal = widget.getWidgetState().getNormal() as Vector3
  
  const sign = normal.filter(v => v !== 0)[0] 
  const origin = sign > 0 ? min : max

  const planeCenter = normal.map((v, i) => {
    if (v === 0) {
      return center[i]
    }
    return origin[i]
  })

  widget.placeWidget(bounds)
  widget.getWidgetState().setOrigin(planeCenter)
  widget.modified()

  const { plane } = widget.get('plane')

  if(!plane) {
    throw new Error('vtkPlane Widget has no member plane!')
  }

  renderer.getActors().forEach(actor => {
    // don't add clipping planes to legends
    if(actor.isA('vktScalarBarActor')) return
    const mapper = actor.getActors()[0].getMapper()
    if(!mapper) return
    mapper.addClippingPlane(plane)
  })
}

export const updateSectionPlanes = (
  bounds: Bounds,
  renderer: vtkRenderer, 
  widgetManager: vtkWidgetManager, 
) => {
  widgetManager.getWidgets().forEach((widget) => {
    if (widget.isA('vtkPlaneWidget')) { 
      updateSectionPlane(widget, bounds, renderer)
    }
  })
}

const addOrientationWidget = (renderer: vtkRenderer, renderWindow: vtkRenderWindow,  widgetManager: vtkWidgetManager, show = true) => {
  // orientation widget
  const axes = compassRoseIndicator()
  
  const orientationWidget = vtkOrientationMarkerWidget.newInstance({
    actor: axes,
    interactor: renderWindow.getInteractor(),
  })
  
  orientationWidget.setEnabled(true)  
  // @ts-ignore
  orientationWidget.setViewportCorner('BOTTOM_LEFT')
  
  orientationWidget.setViewportSize(0.2)
  orientationWidget.setMinPixelSize(100)
  orientationWidget.setMaxPixelSize(250)
  orientationWidget.setParentRenderer(renderer)

  orientationWidget.getRenderer().setDraw(show)

  return { orientationWidget }
}

const DEFAULT_STATE = { renderer: {}, scene: [] }

interface action {
  type: string
  ids?: string[]
  value?: any
}

export const useVTK = (container: React.MutableRefObject<HTMLDivElement>) => {
  const context = useRef<vtkContext | undefined>()
  // https://discourse.vtk.org/t/httpsceneloader-onready-being-called-once-for-each-object/6252
  const loadProgress = useRef(0)

  const [state, setState] = useState(DEFAULT_STATE)

  const dispatch = useCallback((_action?: action | action[], skipRender?: boolean) => {

    if (!context.current) return DEFAULT_STATE

    // @ts-ignore
    const action: action[] | undefined = !_action || typeof _action.length !== 'undefined' ? _action : [_action]

    if(action){
      for(const a of action){
        vtkReducer(context.current, a)
      }
    }

    setState(state => {
      const { renderer, data, renderWindow, widgetManager } = context.current

      const rendererState = deriveVTKState(RENDERER, renderer)

      // const [scalarBarActor] = renderer.getActors().filter(a => a.isA('vtkScalarBarActor'))
      // const legendState = deriveVTKState(LEGEND, scalarBarActor)

      const widgetManagerState = deriveVTKState(WIDGET_MANAGER, widgetManager)

      const sceneState = data.map((dataSource, i) => {

        const derivedState = deriveVTKState(SCENE, dataSource.actor)

        const sceneItem = state.scene.find(item => item.id === dataSource.id)

        // the first time we're deriving state, set id and defaults
        if (!sceneItem) {

          // if hidden field change initial visibility
          if (dataSource.defaultSettings.hidden) {
            dataSource.actor.setVisibility(false)
            derivedState.visibility = false
            if (derivedState.legend != null) {
              const legend = dataSource.actor.get('legend').legend
              legend.setVisibility(false)
            }
          }
          return {
            id: dataSource.id,
            name: dataSource.name,
            defaultSettings: { ...dataSource.defaultSettings },
            state: derivedState
          }
        }

        return {
          ...sceneItem,
          state: derivedState
        }
      })

      // default on
      if (typeof skipRender === 'undefined' || !skipRender) {
        renderWindow.render()
      }

      return {
        renderer: rendererState,
        scene: sceneState,
        widgetManager: widgetManagerState
      } as vtkReactState
    })
  }, [])

  // set up the renderer
  useEffect(() => {
    if (context.current !== undefined) return

    const { renderer, renderWindow, interactor } = renderWindowSetup(container.current)

    const widgetManager = vtkWidgetManager.newInstance()
    widgetManager.setRenderer(renderer)
    widgetManager.enablePicking()

    const { orientationWidget } = addOrientationWidget(renderer, renderWindow, widgetManager, true )

    const branding = brandingWidget.newInstance({
      actor: brandingActor(renderWindow.render),
      interactor: renderWindow.getInteractor(),
      viewportSize: 0.25,
      minPixelSize: 100,
      maxPixelSize: 250,
      parentRenderer: renderer,
      viewportCorner: 'TOP_LEFT'
    })

    branding.setEnabled(true)
    branding.getRenderer().setDraw(true)

    addSectionPlane(renderer, widgetManager, [0,0,1])
    addSectionPlane(renderer, widgetManager, [0,1,0])
    addSectionPlane(renderer, widgetManager, [1,0,0])
    addSectionPlane(renderer, widgetManager, [0,0,-1])
    addSectionPlane(renderer, widgetManager, [0,-1,0])
    addSectionPlane(renderer, widgetManager, [-1,0,0])

    widgetManager.enablePicking()

    // interactor callbacks
    interactor.onKeyPress((e: any) => {
      if (document.activeElement?.nodeName === 'INPUT') return

      if (['r', 'R'].includes(e.key)) {
              
        dispatch({ type: 'reset-camera' })

      } else if (['i', 'I'].includes(e.key)) {
        // isolate / unisolate selected layer(s)
        // will require holding selected state
        // this may be necessary for other effects (like highlight selected in model)
      }
    })

    context.current = {
      renderWindow,
      renderer,
      interactor,
      orientationWidget,
      brandingWidget: branding,
      widgetManager,
      data: [],
    }

    dispatch({ type: 'reset-camera' })

    return () => {
      if (!context.current) return

      const {
        renderer, 
        renderWindow, 
        interactor, 
        orientationWidget, 
        brandingWidget,
        widgetManager
      } = context.current

      renderer.removeAllActors()
      renderer.delete()
      renderWindow.delete()
      interactor.delete()
      orientationWidget.delete()
      brandingWidget.delete()
      widgetManager.delete()
      context.current.data = undefined // necessary?
      context.current = undefined
    }
  }, [container, dispatch])

  // load a file that vtk.js can interpret
  // TODO: Move this whole loading flow to reducers (?)
  // feels cleaner, not sure what the benefit would be tho
  // dispatch({type: 'load-vtkjs: value: file})
  const loadFile = useCallback(async (file: Blob, type: string, config?: Partial<vtkReactState>) => {
    if (!context.current) return

    const { renderer, renderWindow, widgetManager } = context.current

    loadProgress.current = 0

    switch (type) {
      case 'vtp': {
        file.arrayBuffer().then((buffer) => {
          const vtpReader = vtkXMLPolyDataReader.newInstance()
          vtpReader.parseAsArrayBuffer(buffer)

          const source = vtpReader.getOutputData(0)

          const mapper = vtkMapper.newInstance({
            interpolateScalarsBeforeMapping: false,
            useLookupTableScalarRange: true,
            scalarVisibility: false,
          })

          //@ts-ignore
          mapper.setInputData(source)

          const actor = vtkActor.newInstance()

          actor.setMapper(mapper)

          renderer.addActor(actor)

          const property = actor.getProperty()
          property.setDiffuseColorFrom([0.65, 0.65, 0.65])

          context.current.data.push({
            name: 'Default',
            id: uuid(),
            source,
            actor,
            mapper,
            defaultSettings: {}
          })

          renderer.resetCamera()
          renderWindow.render()

        }).then(() => {
          dispatch({ type: 'reset-camera' })
        })
        return
      }
      case 'vtkjs': {
        // @ts-ignore
        const dataAccessHelper = get('zip', {
          zipContent: file,
          callback: (zip: any) => {
            // @ts-ignore this is weird
            const sceneImporter = HttpSceneLoader.newInstance({ renderer, dataAccessHelper, startLODLoaders: false })

            sceneImporter.setUrl('index.json')

            // onReady is called once for each object
            // https://discourse.vtk.org/t/httpsceneloader-onready-being-called-once-for-each-object/6252
            // @ts-ignore sceneImporter.onReady type is weird
            sceneImporter.onReady(() => {

              const imported = sceneImporter.getScene()

              loadProgress.current += 1

              // @ts-ignore type of imported incorrect
              if (loadProgress.current === imported.length) {
                const slots = ['RIGHT', 'BOTTOM', 'TOP', 'LEFT']

                let slotIndex = 0

                // @ts-ignore
                imported.forEach((dataSource) => {
  
                  if (!dataSource.id) dataSource.id = uuid()
                  if (dataSource.source.getArrays().length > 0) {
                    configureData(dataSource, renderer, slots[slotIndex])
                    slotIndex = (slotIndex + 1) % slots.length
                  }
                  context.current.data.push(dataSource)
                })

                updateSectionPlanes(renderer.computeVisiblePropBounds(), renderer, widgetManager)

                if (config) {
                  const { renderer: _renderer, scene: _scene } = config
                  if (_renderer) {
                    const { background: _background, camera: _camera } = _renderer
                    if(_background) renderer.setBackgroundFrom(_background)
                    if(_camera) {
                      const camera = renderer.getActiveCamera()
                      if(_camera.focalPoint) {
                        const [fpx, fpy, fpz] = _camera.focalPoint
                        camera.setFocalPoint(fpx, fpy, fpz)
                      }
                      if(typeof _camera.parallelProjection !== 'undefined') {
                        camera.setParallelProjection(_camera.parallelProjection)
                      }
                      if(_camera.position) {
                        const [cx, cy, cz] = _camera.position
                        camera.setPosition(cx, cy, cz)
                      }
                      if(_camera.viewUp) {
                        camera.setViewUpFrom(config.renderer.camera.viewUp)
                      }
                      // if camera is included in config, no reset
                      dispatch()
                      return
                    }
                  }
                }
                dispatch({ type: 'reset-camera' })
              }
            })
          },
        })
        return
      }
      default:
        throw new Error(`Unsupported file type: ${type}`)
    }
  }, [dispatch])

  // let's try this
  const loadFileDebounced = useCallback(debounce((file:Blob, type: string, config?: Partial<vtkReactState>) => loadFile(file, type, config), 500), [loadFile])

  return { context, loadFile: loadFileDebounced, state, dispatch, fastLoading: loadFile }
}
